"use client";
import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import search from "@/public/images/search.svg";
import { useForm } from "react-hook-form";
import ArticleCard from "@/components/shared/ArticleCard";
import { useGetAllGeneralFacultiesWithFiltersQuery } from "@/redux/features/api/website/websiteApiSlice";
import { useTranslate } from "@/components/base/providers/TranslateProv";
import { Blog } from "@/types/blog";
import Loader from "@/components/shared/Loader";
import Pagination from "@/components/shared/Pagination";
import FacultyCard from "@/components/shared/FacultyCard";
import { Faculty } from "@/types/Faculty";

type SearchFormInputs = {
  search: string;
};

const Page = () => {
  const { locale } = useTranslate();
  const lang = locale as "ar" | "en";

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;
  const [searchQuery, setSearchQuery] = useState<string>("");

  const { register, handleSubmit } = useForm<SearchFormInputs>({
    defaultValues: {
      search: searchQuery,
    },
  });

  const { data, isLoading, error, isFetching, refetch } =
    useGetAllGeneralFacultiesWithFiltersQuery({
      search: searchQuery,
      page: currentPage,
      limit: itemsPerPage,
    });

  const translations = {
    ar: {
      mainTitle: "الكليات العامه",
      subtitle:
        "ابحث عن أفضل الجامعات ومعاهد اللغات في العالم ومراجعتها والتقديم إليها",
      searchPlaceholder: "ابحث عن كليه معينة هنا",
      searchResults: "نتائج البحث",
    },
    en: {
      mainTitle: "General Faculties",
      subtitle:
        "Search for the best universities and language institutes worldwide, review them, and apply",
      searchPlaceholder: "Search for a specific Faculty here",
    },
    searchResults: "Search Results",
  };

  const t = translations[lang];

  const onSubmit = (data: SearchFormInputs) => {
    setSearchQuery(data.search);
    refetch();
  };

  const handlePageChange = (selectedItem: { selected: number }) => {
    setCurrentPage(selectedItem.selected + 1);
    refetch();
  };
  const resultsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentPage]);
  return (
    <>
      <div
        className="bg-primaryColor pt-10 pb-32 flex flex-col gap-5 mb-28"
        ref={resultsRef}
      >
        <p className="text-36 text-white font-[500] mb-5 text-center">
          {t.mainTitle}
        </p>
        <p className="text-18 text-white font-[500] mb-3 text-center">
          {t.subtitle}
        </p>
        <div className="relative container w-full mt-20 bg-white shadow-xl p-5 rounded-[10px] mx-auto -mb-[11rem]">
          <form onSubmit={handleSubmit(onSubmit)} className="relative">
            <input
              type="text"
              {...register("search")}
              className={`w-full h-[60px] bg-[#F8F9FA] rounded-[10px] py-3 px-14 ${
                lang === "ar" ? "text-right" : "text-left"
              }`}
              placeholder={t.searchPlaceholder}
            />
            <Image
              src={search}
              alt="search"
              className={`absolute top-1/2 -translate-y-1/2 ${
                lang === "ar" ? "right-5" : "left-5"
              }`}
            />
          </form>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center items-center h-[300px]">
          <Loader />
        </div>
      )}
      {error && (
        <div className="flex justify-center items-center h-[300px]">
          <p className="text-center text-20 text-red-500 font-[600]">
            {lang === "ar" ? "خطأ في جلب البيانات." : "Error fetching data."}
          </p>
        </div>
      )}
      <div className="container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 my-5">
        {!isLoading &&
          !error &&
          data?.data?.data?.map((fac: Faculty, index: number) => (
            <FacultyCard key={index} faculty={fac} locale={lang} />
          ))}
      </div>
      {data?.data?.paginate?.totalPages > 1 && (
        <Pagination
          pageCount={data?.data?.paginate?.totalPages}
          onPageChange={handlePageChange}
          currentPage={currentPage}
        />
      )}
    </>
  );
};

export default Page;
